"use client";
import type { Media, OmniboxDemo } from "@mw/shared/payload-types";
import type React from "react";

import { EventName } from "@mw/shared/analyticsEventNames";
import { appUrls, publicUrls } from "@/constants";
import { env } from "@/env";
import { Tabs, TabsContent } from "@components/core/Tabs";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { cn } from "@ui/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";

import type { SampleEventDetail } from "../core/SampleButton";

import OmniboxCTA from "./OmniboxCTA";
import { ScrollableTabs } from "./OmniboxScrollableTabs";

const PRODUCT_CTA_LINKS: Partial<Record<OmniboxDemo["component"], string>> = {
  tts: appUrls.textToSpeech,
  sts: appUrls.voiceChanger,
  dubbing: appUrls.dubbing,
  ttsfx: appUrls.soundEffects,
  voiceCloning: appUrls.voices,
  stt: appUrls.speechToText,
  convAI: appUrls.convAI,
  elevenReader: appUrls.elevenReader,
  music: appUrls.music,
};

interface OmniboxProps {
  childrenBelowDemos?: React.ReactNode;
  className?: string;
  ctaLink?: string;
  // New structure with components included
  demos?: Array<{
    Component: React.ReactNode;
    component: OmniboxDemo["component"];
    icon?: Media | null | number | string | undefined;
    title: string;
  }>;
  hideCta?: boolean;
  size?: "2xl" | "md" | "xl";
}

export default function Omnibox({
  demos = [],
  ctaLink = appUrls.root,
  size = "md",
  hideCta = false,
  className,
  childrenBelowDemos,
}: OmniboxProps) {
  const [tab, setTab] = useState(demos[0]?.component || "tts");
  const containerRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();

  const handleSelection = (index: number, component: OmniboxDemo["component"]) => {
    setTab(component);
    analytics.captureEvent(EventName.website_clicked_omnibox_tab, { tabComponentName: component });
  };

  function autoPlay(e: CustomEvent<SampleEventDetail>) {
    const { sampleType } = e.detail;
    setTab(sampleType);
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  useEffect(() => {
    document.addEventListener("sample", autoPlay as EventListener);
    return () => {
      document.removeEventListener("sample", autoPlay as EventListener);
    };
  }, []);

  const dynamicCtaLink = useMemo(() => PRODUCT_CTA_LINKS[tab] || ctaLink, [tab, ctaLink]);
  const signupWithRedirectLink = useMemo(() => {
    // Eleven reader is on a separate domain, so we need to redirect to it
    if (tab === "elevenReader") {
      return appUrls.elevenReader;
    }
    // Make the redirect relative to the app url
    const relativePath = dynamicCtaLink.replace(env.NEXT_PUBLIC_WEB_APP_URL, "/app");
    return `${publicUrls.signUp}?redirect=${encodeURIComponent(relativePath)}`;
  }, [dynamicCtaLink, tab]);

  const demoTabs = useMemo(() => {
    return demos.map(({ component, icon, title }) => ({ component, icon, title }));
  }, [demos]);

  return (
    <div
      className={cn(
        "container flex justify-center",
        {
          "mt-32": size === "xl" || size === "2xl",
        },
        className,
      )}
      ref={containerRef}
    >
      <div
        className={cn("w-12-cols @container", {
          "lg:w-8-cols": size === "md",
          "lg:w-10-cols": size === "xl",
          "lg:w-12-cols": size === "2xl",
        })}
      >
        <Tabs className='flex flex-col' defaultValue='tts' value={tab}>
          {demos.length > 1 && <ScrollableTabs activeTab={tab} demos={demoTabs} onTabSelect={handleSelection} />}
          {demos.map(({ component, Component }) => (
            <TabsContent
              asChild
              className='outline-none'
              key={`component-${component}`}
              tabIndex={-1}
              value={component}
            >
              <div>{Component}</div>
            </TabsContent>
          ))}
        </Tabs>
        {childrenBelowDemos}
        {!hideCta && <OmniboxCTA signupWithRedirectLink={signupWithRedirectLink} tab={tab} />}
      </div>
    </div>
  );
}
